import React, { useState } from 'react';
import {
  Container,
  Header,
  Input,
  FormField,
  SpaceBetween,
  Button,
  Table,
  Box,
  Link,
  Icon,
  Badge,
  Spinner,
  Alert,
  Toggle
} from '@cloudscape-design/components';
import { list, getProperties } from 'aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import s3Service from '../services/s3Service';
import config from '../config';

const SearchView = ({ userHomePath, onVideoSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [useSemanticSearch, setUseSemanticSearch] = useState(true);
  const [error, setError] = useState('');

  const handleSemanticSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setError('');
    
    try {
      const session = await fetchAuthSession();
      
      const lambda = new LambdaClient({
        region: config.Region,
        credentials: session.credentials
      });

      const command = new InvokeCommand({
        FunctionName: config.KnowledgeBaseQueryFunctionArn,
        Payload: JSON.stringify({
          query: searchQuery,
          maxResults: 10
        })
      });

      const response = await lambda.send(command);
      const result = JSON.parse(new TextDecoder().decode(response.Payload));
      
      console.log('Lambda response:', result);
      
      if (result.statusCode === 200) {
        const data = JSON.parse(result.body);
        
        console.log('Parsed data:', data);
        
        // Map semantic search results to video files
        const results = [];

        for (const item of data.results) {
          // Extract S3 key from location
          const s3Uri = item.location?.s3Location?.uri;
          
          if (s3Uri) {
            // Parse S3 URI: s3://bucket-name/key
            const s3Key = s3Uri.replace(/^s3:\/\/[^\/]+\//, '');
            const kbBucket = s3Uri.match(/s3:\/\/([^\/]+)/)[1];
            
            try {
              // Get tags from the KB file to find the original recording
              const tags = await s3Service.getTags(kbBucket, s3Key);
              const recordingKey = tags['recordings-file-key'];
              
              if (recordingKey) {
                // Verify recording still exists before adding to results
                try {
                  const properties = await getProperties({ path: recordingKey });
                  const displayName = properties.metadata?.['display-name'] || recordingKey.split('/').pop();
                  const timestamp = properties.metadata?.timestamp;

                  const recordingTags = await s3Service.getTags(config.Bucket, recordingKey);
                  const status = recordingTags.status || 'Error';

                  results.push({
                    name: recordingKey.split('/').pop(),
                    displayName,
                    key: recordingKey,
                    type: 'file',
                    lastModified: properties.lastModified,
                    size: properties.size,
                    isVideo: true,
                    status,
                    timestamp,
                    matchedContent: item.content.substring(0, 200) + '...'
                  });
                } catch (recordingErr) {
                  // Recording doesn't exist anymore, skip this result
                  console.log(`Recording not found, skipping: ${recordingKey}`);
                }
              }
            } catch (err) {
              console.error(`Error fetching recording for ${s3Key}:`, err);
            }
          }
        }

        setSearchResults(results);
      } else {
        throw new Error(result.body || 'Failed to search');
      }
    } catch (err) {
      console.error('Error performing semantic search:', err);
      setError('Semantic search failed. Please try again or use filename search.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleFilenameSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setError('');
    
    try {
      const allItems = await list({
        path: `${userHomePath}/`,
        options: { listAll: true }
      });

      const videoFiles = allItems.items.filter(item => 
        item.path.toLowerCase().endsWith('.webm')
      );

      const results = [];
      for (const item of videoFiles) {
        try {
          const properties = await getProperties({ path: item.path });
          const displayName = properties.metadata?.['display-name'] || item.path.split('/').pop();
          const timestamp = properties.metadata?.timestamp;

          if (displayName.toLowerCase().includes(searchQuery.toLowerCase())) {
            // Get status from tags using service
            const tags = await s3Service.getTags(config.Bucket, item.path);
            const status = tags.status || 'Error';

            results.push({
              name: item.path.split('/').pop(),
              displayName,
              key: item.path,
              type: 'file',
              lastModified: item.lastModified,
              size: item.size,
              isVideo: true,
              status,
              timestamp
            });
          }
        } catch (err) {
          console.error(`Error fetching properties/tags for ${item.path}:`, err);
        }
      }

      results.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching:', err);
      setError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = () => {
    if (useSemanticSearch) {
      handleSemanticSearch();
    } else {
      handleFilenameSearch();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === null || bytes === undefined) return '';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const columnDefinitions = [
    {
      id: 'name',
      header: 'Name',
      cell: item => (
        <Link href="#" onFollow={() => onVideoSelect(item)}>
          <Icon name="video" /> {item.displayName}
        </Link>
      ),
      sortingField: 'displayName',
      isRowHeader: true
    },
    {
      id: 'status',
      header: 'Status',
      cell: item => {
        const statuses = item.status.split(',').map(s => s.trim());
        return (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            {statuses.map((status, index) => (
              <Badge
                key={index}
                color={
                  status.toLowerCase() === 'completed' ? 'green' :
                  status.toLowerCase().includes('error') ? 'red' : 'blue'
                }
              >
                {status}
              </Badge>
            ))}
          </div>
        );
      }
    },
    {
      id: 'size',
      header: 'Size',
      cell: item => formatFileSize(item.size),
      sortingField: 'size'
    },
    {
      id: 'timestamp',
      header: 'Created',
      cell: item => formatDate(item.timestamp),
      sortingField: 'timestamp'
    }
  ];

  return (
    <Container variant='embedded'
      header={
        <Header 
          variant="h1" 
          description={useSemanticSearch ? 
            "Search through transcript content using AI-powered semantic search" : 
            "Search through all your recordings by name"
          }
        >
          Search Recordings
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Box padding={{ top: 's', bottom: 's' }}>
          <SpaceBetween size="m">
            <Toggle
              checked={useSemanticSearch}
              onChange={({ detail }) => setUseSemanticSearch(detail.checked)}
            >
              <Box variant="strong">
                {useSemanticSearch ? '🔍 Semantic Search (AI-powered): ' : '📝 Filename Search: '}
              </Box>
              <Box variant="small" color="text-body-secondary">
                {useSemanticSearch ? 
                  'Search by meaning and context in transcripts' : 
                  'Search by recording name only'
                }
              </Box>
            </Toggle>
            
            <FormField stretch={true}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <Input
                    value={searchQuery}
                    onChange={({ detail }) => setSearchQuery(detail.value)}
                    placeholder={useSemanticSearch ? 
                      "Ask a question or describe what you're looking for..." : 
                      "Search by recording name..."
                    }
                    onKeyDown={(e) => e.detail.key === 'Enter' && handleSearch()}
                    disabled={isSearching}
                    type="search"
                    inputMode="search"
                  />
                </div>
                <Button
                  variant="primary"
                  onClick={handleSearch}
                  loading={isSearching}
                  disabled={!searchQuery.trim()}
                  iconName="search"
                >
                  Search
                </Button>
              </div>
            </FormField>
          </SpaceBetween>
        </Box>

        {error && (
          <Alert type="warning" dismissible onDismiss={() => setError('')}>
            {error}
          </Alert>
        )}

        {isSearching && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
            <Box variant="p" padding={{ top: 's' }}>
              {useSemanticSearch ? 'Searching transcripts with AI...' : 'Searching recordings...'}
            </Box>
          </Box>
        )}

        {!isSearching && searchResults.length > 0 && (
          <Table
            columnDefinitions={columnDefinitions}
            items={searchResults}
            empty={
              <Box textAlign="center" padding="l">
                <b>No results found</b>
              </Box>
            }
            header={
              <Header counter={`(${searchResults.length})`}>
                Search Results
              </Header>
            }
          />
        )}

        {!isSearching && searchQuery && searchResults.length === 0 && (
          <Box textAlign="center" padding="l">
            <Box variant="p" color="text-status-inactive">
              <Icon name="search" size="big" />
            </Box>
            <Box variant="h3" padding={{ top: 's' }}>
              No recordings found
            </Box>
            <Box variant="p" color="text-body-secondary">
              Try adjusting your search terms
            </Box>
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default SearchView;
